import { useState, useEffect, useCallback } from 'react';
import { Movie, FormattedMovie } from '../types/movie';
// Import the actual client and the utility functions including the new getMoviesByIds
import { sanityClient, getCurrentMovies, getUpcomingMovies, getFutureMovies, urlFor, getMoviesByIds, getRecentTrailers, getAllTrailers, TrailerWithMovie } from '../utils/sanityClient';

const formatMovie = (movie: Movie): FormattedMovie => ({
  ...movie,
  poster: urlFor(movie.poster),
  releaseDate: new Date(movie.releaseDate).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }),
  hypeScore: movie.hypeLevel
})

export function useCurrentMovies() {
  const [movies, setMovies] = useState<FormattedMovie[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchMovies = useCallback(async () => {
    try {
      const data = await getCurrentMovies()
      if (!Array.isArray(data)) {
        throw new Error('Invalid response format from Sanity')
      }
      setMovies(data.map(formatMovie))
      setLoading(false)
    } catch (err) {
      console.error('Error fetching movies:', err)
      setError(err instanceof Error ? err : new Error('Failed to fetch movies'))
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchMovies()
  }, [fetchMovies])

  return { 
    movies, 
    loading, 
    error,
    refresh: () => {
      setLoading(true)
      setError(null)
      fetchMovies()
    }
  }
}

export function useUpcomingMovies() {
  const [movies, setMovies] = useState<FormattedMovie[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchMovies = useCallback(async () => {
    try {
      const data = await getUpcomingMovies()
      if (!Array.isArray(data)) {
        throw new Error('Invalid response format from Sanity')
      }
      setMovies(data.map(formatMovie))
      setLoading(false)
    } catch (err) {
      console.error('Error fetching movies:', err)
      setError(err instanceof Error ? err : new Error('Failed to fetch movies'))
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchMovies()
  }, [fetchMovies])

  return { 
    movies, 
    loading, 
    error,
    refresh: () => {
      setLoading(true)
      setError(null)
      fetchMovies()
    }
  }
}

export function useFutureMovies(limit: number = 10) {
  const [movies, setMovies] = useState<FormattedMovie[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [hasMore, setHasMore] = useState(true)
  const [offset, setOffset] = useState(0)

  const fetchMovies = useCallback(async (currentOffset: number, isLoadMore: boolean = false) => {
    try {
      const data = await getFutureMovies(limit, currentOffset)
      if (!Array.isArray(data)) {
        throw new Error('Invalid response format from Sanity')
      }
      
      const formattedMovies = data.map(movie => ({
        ...movie,
        releaseDate: new Date(movie.releaseDate).toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        }),
        hypeScore: movie.hypeLevel || 0,
        poster: '',
        posterCopyright: '',
        synopsis: '',
        hypeLevel: movie.hypeLevel || 0
      }))
      
      if (isLoadMore) {
        setMovies(prev => [...prev, ...formattedMovies])
      } else {
        setMovies(formattedMovies)
      }
      
      setHasMore(data.length === limit)
      setLoading(false)
    } catch (err) {
      console.error('Error fetching movies:', err)
      setError(err instanceof Error ? err : new Error('Failed to fetch movies'))
      setLoading(false)
    }
  }, [limit])

  useEffect(() => {
    fetchMovies(0)
  }, [fetchMovies])

  return {
    movies,
    loading,
    error,
    hasMore,
    loadMore: () => {
      if (!loading && hasMore) {
        setLoading(true)
        setError(null)
        const newOffset = offset + limit
        setOffset(newOffset)
        fetchMovies(newOffset, true)
      }
    }
  }
}

export function useMoviesByIds(ids: string[]) {
  const [movies, setMovies] = useState<FormattedMovie[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchMoviesByIds = useCallback(async () => {
    if (!ids || ids.length === 0) {
      setMovies([]);
      setLoading(false);
      setError(null); // Clear previous errors
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Use the directly imported getMoviesByIds function
      const data = await getMoviesByIds(ids);
      if (!Array.isArray(data)) {
        throw new Error('Invalid response format from Sanity when fetching by IDs');
      }
      setMovies(data.map(formatMovie));
    } catch (err) {
      console.error('Error fetching movies by IDs:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch movies by IDs'));
    } finally {
      setLoading(false);
    }
  }, [ids.join(',')]); // Depend on a string representation of ids for stability

  useEffect(() => {
    fetchMoviesByIds();
  }, [fetchMoviesByIds]);

  return {
    movies,
    loading,
    error,
    refresh: fetchMoviesByIds
  };
}

export function useRecentTrailers(limit: number = 10) {
  const [trailers, setTrailers] = useState<TrailerWithMovie[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchTrailers = useCallback(async () => {
    try {
      const data = await getRecentTrailers(limit);
      setTrailers(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching recent trailers:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch trailers'));
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchTrailers();
  }, [fetchTrailers]);

  return {
    trailers,
    loading,
    error,
    refresh: () => {
      setLoading(true);
      setError(null);
      fetchTrailers();
    }
  };
}

export function useAllTrailers(limit: number = 20) {
  const [trailers, setTrailers] = useState<TrailerWithMovie[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [mainOnly, setMainOnly] = useState(false);

  const fetchTrailers = useCallback(
    async (currentOffset: number, isLoadMore: boolean = false, filterMainOnly: boolean = false) => {
      try {
        const data = await getAllTrailers(limit, currentOffset, filterMainOnly);
        if (isLoadMore) {
          setTrailers((prev) => [...prev, ...data]);
        } else {
          setTrailers(data);
        }
        setHasMore(data.length === limit);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching all trailers:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch trailers'));
        setLoading(false);
      }
    },
    [limit]
  );

  useEffect(() => {
    setLoading(true);
    setError(null);
    setOffset(0);
    fetchTrailers(0, false, mainOnly);
  }, [fetchTrailers, mainOnly]);

  return {
    trailers,
    loading,
    error,
    hasMore,
    mainOnly,
    setMainOnly: (value: boolean) => {
      setMainOnly(value);
    },
    loadMore: () => {
      if (!loading && hasMore) {
        setLoading(true);
        setError(null);
        const newOffset = offset + limit;
        setOffset(newOffset);
        fetchTrailers(newOffset, true, mainOnly);
      }
    },
    refresh: () => {
      setLoading(true);
      setError(null);
      setOffset(0);
      fetchTrailers(0, false, mainOnly);
    },
  };
}